import type { CogniteClient, NodeDefinition } from '@cognite/sdk';

import type { DataModelView, MintSeriesSource } from '#core/lib/cognite/view.ts';
import type { SourceFetcherCtx } from '#core/lib/expr/EvalContext.ts';
import type { Cell } from '#core/lib/expr/value.ts';
import type { InstanceIdType, TimeSeriesRefType } from '#core/schema/defs.ts';
import { extractTsRef } from '#core/schema/utils.ts';

import { unitForKind } from './columnUnits.ts';
import type { EnrichSpec } from './createEnrichedView.ts';
import { type AncestorRelation, asRef, enrichWithAncestors } from './enrichAncestors.ts';
import type { MeasureRefs } from './fetchDatapointsLong.ts';
import { resolveColumnSet } from './resolveColumns.ts';
import {
  type EnrichGroup,
  isResolvedStruct,
  type Resolved,
  type ResolvedFlat,
  resolve,
  type Shape,
} from './shapes/index.ts';

/**
 * The fields a datapoint-exploding view spec shares regardless of its assembly
 * step (long flattening vs timestamp alignment): the registry name + docs, the
 * source view, the kept context columns, optional hierarchy enrichment, the
 * rolling-window size, and the client + timeseries minter the reads run
 * through. The measure-handle group is named distinctly per factory
 * (`measures` for flattening, `groups` for alignment), so it stays off the base.
 */
export type ExplosionViewSpecBase = {
  /** Agent-facing registry name (distinct from the source externalId). */
  readonly name: string;
  /** View-level description shown to the agent by the DescribeViews tool. */
  readonly description: string;
  /** The source CDF view this explodes. */
  readonly source: DataModelView;
  /** Context columns broadcast onto every exploded row (identity, relations, category). */
  readonly keep: Shape;
  /** Optional hierarchy enrichment broadcast as ancestor struct columns per row. */
  readonly enrich?: Required<Pick<EnrichSpec, 'stringRefColumn' | 'relations'>>;
  /** Rolling-window size (days) for the raw datapoints read; defaults to `TS_REF_DEFAULT_WINDOW_DAYS`. */
  readonly defaultWindowDays?: number;
  /** Client used for the source read + the datapoints retrieve. */
  readonly client: CogniteClient;
  /**
   * The timeseries minter (`useSessionTimeseries().buildSource`) the context /
   * hierarchy reads project through. Optional only because `buildSource` is
   * optional on the provider type; the fetch eager-throws if it is absent.
   */
  readonly mintSeries?: MintSeriesSource;
};

/**
 * The flat kept-context columns and the flat measure-handle columns a
 * datapoint-exploding view reads from its source. A source column kept as
 * broadcast context is never re-projected as a measure handle — otherwise it
 * would explode into spurious extra datapoint rows.
 */
export type ExplosionColumns = {
  /** The kept context columns, de-prefixed/renamed source ⇄ canonical. */
  readonly keepCols: readonly ResolvedFlat[];
  /** The per-row timeseries-handle columns flattened into datapoint rows. */
  readonly measureCols: readonly ResolvedFlat[];
};

/**
 * Resolve a view's `keep` + `measures` shapes into the flat kept-context and
 * measure-handle column sets every datapoint-exploding factory shares. Both the
 * long flattening and the timestamp-aligned flattening read identical source
 * columns; they differ only in how they assemble the fetched datapoints.
 */
export const resolveExplosionColumns = (
  source: DataModelView,
  keep: Shape,
  measures: EnrichGroup,
): ExplosionColumns => {
  const { cols } = resolveColumnSet(source, keep);
  const keepCols: readonly ResolvedFlat[] = cols.filter((c: Resolved): c is ResolvedFlat => !isResolvedStruct(c));
  const keptSources = new Set(keepCols.map((c: ResolvedFlat) => c.source));
  const measureCols: readonly ResolvedFlat[] = resolve(measures.shape, measures.prefix).filter(
    (c: Resolved): c is ResolvedFlat => !isResolvedStruct(c) && !keptSources.has(c.source),
  );
  return { keepCols, measureCols };
};

/**
 * The source columns a datapoint-exploding view projects from its source: the
 * kept context columns plus the measure handle columns.
 */
export const explosionSourceColumns = ({ keepCols, measureCols }: ExplosionColumns): readonly string[] => [
  ...keepCols.map((c: ResolvedFlat) => c.source),
  ...measureCols.map((c: ResolvedFlat) => c.source),
];

/**
 * Read a per-row timeseries handle off a raw CDF node row for `sourceColumn`,
 * mirroring `projectCell`'s nested-dataset branch: the declared view
 * properties nest under `properties[space][externalId/version][col]`, and a
 * CogniteTimeSeries reference is either a bare externalId string or a
 * `{ space?, externalId }` object — both decoded by `extractTsRef`.
 */
export const rowTsRef = (row: NodeDefinition, view: DataModelView, sourceColumn: string): TimeSeriesRefType | null => {
  const viewKey = `${view.externalId}/${view.version}`;
  const props = row.properties as Record<string, Record<string, Record<string, unknown>> | undefined> | undefined;
  const value = props?.[view.space]?.[viewKey]?.[sourceColumn];
  return extractTsRef(value);
};

/**
 * Build the per-measure, per-source-row timeseries handles, read RAW off the
 * node rows. `refs[i]` is source row `i`'s handle for that measure (null when
 * the row carries no series for it). Each measure also carries its resolved
 * `storedUnit` slug — derived from the column's declared measurement category
 * via {@link unitForKind} — so the datapoint materialization boundary can
 * normalize stored values to the canonical base.
 */
export const buildMeasureRefs = (
  rows: readonly NodeDefinition[],
  source: DataModelView,
  measureCols: readonly ResolvedFlat[],
): readonly MeasureRefs[] =>
  measureCols.map(
    (c: ResolvedFlat): MeasureRefs => ({
      canonical: c.canonical,
      refs: rows.map((row: NodeDefinition) => rowTsRef(row, source, c.source)),
      storedUnit: unitForKind(c.kind),
    }),
  );
/**
 * Broadcast a source-grain column onto a row stream by each row's owning
 * source-row index: `owners[i]` is the source row output row `i` was expanded
 * from. The two datapoint-exploding factories differ only in where the owner
 * indices come from (long `rowOwners` vs aligned `RowSeries.owner`), so each
 * passes the resolved owner list and shares the per-cell broadcast.
 */
export const broadcastByOwner =
  (owners: readonly number[]) =>
  (col: readonly Cell[]): readonly Cell[] =>
    owners.map((owner: number) => col[owner] ?? null);

/**
 * Resolve the optional hierarchy ancestry once over the source rows, keyed by
 * relation, ready to broadcast per owning source-row index alongside the kept
 * context. The producing-string refs are read off the already-projected context
 * column named by `enrich.stringRefColumn`; returns `{}` when no enrichment or
 * no relations are requested.
 */
export const resolveAncestorsByRelation = async ({
  client,
  ctx,
  context,
  enrich,
  relations,
  mintSeries,
}: {
  readonly client: CogniteClient;
  readonly ctx: SourceFetcherCtx;
  readonly context: Record<string, readonly Cell[]>;
  readonly enrich: ExplosionViewSpecBase['enrich'];
  readonly relations: readonly AncestorRelation[];
  readonly mintSeries: MintSeriesSource;
}): Promise<Partial<Record<AncestorRelation, readonly Cell[]>>> => {
  if (enrich === undefined || relations.length === 0) {
    return {};
  }
  const stringCells: readonly Cell[] = context[enrich.stringRefColumn] ?? [];
  const stringRefs: readonly (InstanceIdType | null)[] = stringCells.map(asRef);
  const resolved = await enrichWithAncestors(client, ctx, stringRefs, relations, mintSeries);
  return Object.fromEntries(relations.map((rel: AncestorRelation) => [rel, resolved[rel]] as const));
};

/**
 * Assemble the broadcast kept-context columns and ancestor-relation columns
 * shared by both datapoint-exploding factories: each kept context column and
 * each resolved ancestor column projected onto the row stream via `broadcast`.
 */
export const assembleContextAndAncestorColumns = ({
  keepCols,
  relations,
  context,
  ancestorsByRel,
  broadcast,
}: {
  readonly keepCols: readonly ResolvedFlat[];
  readonly relations: readonly AncestorRelation[];
  readonly context: Record<string, readonly Cell[]>;
  readonly ancestorsByRel: Partial<Record<AncestorRelation, readonly Cell[]>>;
  readonly broadcast: (col: readonly Cell[]) => readonly Cell[];
}): Record<string, readonly Cell[]> => ({
  ...Object.fromEntries(
    keepCols.map((c: ResolvedFlat) => [c.canonical, broadcast(context[c.canonical] ?? [])] as const),
  ),
  ...Object.fromEntries(relations.map((rel: AncestorRelation) => [rel, broadcast(ancestorsByRel[rel] ?? [])] as const)),
});
